import { AfterViewInit, Component, ElementRef, EventEmitter, forwardRef, Input, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BlobType } from '@ca/core/models';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import Quill from 'quill';
import { getWithoutHtmlTags } from 'src/utils/text/text-utils';
import { FileUploadDialogComponent } from '../file-upload-dialog/file-upload-dialog.component';

/**
 Quill library has no type inffered. Using any for FontAttributor as denoted in Quill library.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FontAttributor: any = Quill.import('attributors/class/font');
FontAttributor.whitelist = ['IBMPlexSans'];
// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
Quill.register(FontAttributor, true);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef((): typeof QuillEditorWrapperComponent => QuillEditorWrapperComponent),
    multi: true,
};

@Component({
    selector: 'ca-quill-editor-wrapper',
    templateUrl: './quill-editor-wrapper.component.html',
    styleUrls: ['./quill-editor-wrapper.component.scss'],
    providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
})
export class QuillEditorWrapperComponent implements AfterViewInit, ControlValueAccessor {
    @Input() maxLength?: number;
    @Input() placeholder?: string;
    @Input() modules?: Object;
    @Input() readOnly?: boolean;
    @Input() required?: boolean;

    @Output() touched = new EventEmitter<void>();

    value: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    quillEditorRef: any;

    @ViewChild('quillEditor', { read: ElementRef }) private quillEditorDomElement: ElementRef<HTMLElement>;

    private readonly maxInputSizeForRichText: number = 2 * 1024 * 1024;

    // Quill editor toolbar configurations
    private defaultQuillModules = {
        toolbar: {
            container: [
                [{ font: ['IBMPlexSans'] }],
                [{ size: ['small', false, 'large', 'huge'] }],
                ['bold', 'italic', 'underline', 'strike'],
                ['blockquote', 'code-block'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ script: 'sub' }, { script: 'super' }],
                [{ indent: '-1' }, { indent: '+1' }],
                [{ direction: 'rtl' }],
                [{ align: [] }],
                ['clean'],
                ['link', 'video', 'image'],
            ],
            handlers: {
                // handlers object will be merged with default handlers object
                image: (): void => {
                    this.customImageUpload();
                },
            },
        },
        ImageResize: {},
        imageDropAndPaste: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            handler: ($event: any): void => this.uploadBase64ImageToBlob($event as string),
        },
    };

    get quillModules(): object {
        if (this.modules != null) {
            return this.modules;
        } else {
            return this.defaultQuillModules;
        }
    }

    constructor(
        private translateService: TranslateService,
        private modalService: NgbModal,
    ) {}

    ngAfterViewInit(): void {
        this.showTooltips();
    }

    onChange(value: string): void {
        this.value = value;
    }

    onClick(): void {
        // do nothing
    }

    onSelectionChange(event): void {
        if (this.value) {
            if (getWithoutHtmlTags(this.value)?.length === 0) {
                this.value = ''; // sets the value inside the wrapper
                this.onChange(''); // sets the value inside the component that is using the wrapper
            }
        }
        // mark it as touched if you
        // have clicked inside the editor AND there already was a text
        // OR if you have left the quill editor
        if ((event.oldRange === null && event.range?.index) || !event.range) {
            this.touched.emit();
        }
    }

    writeValue(value: string): void {
        this.value = value;
    }

    onTouched(): void {
        // do nothing
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setUpEditor(instance: any): void {
        this.quillEditorRef = instance;
    }

    private showTooltips(): void {
        const showTooltip = (which, el) => {
            let tool: string | undefined;
            if (which === 'button') {
                tool = el.className.replace('ql-', '');
            } else if (which === 'span') {
                tool = el.className.replace('ql-', '');
                tool = tool?.substr(0, tool?.indexOf(' '));
            }
            if (tool) {
                // if element has value attribute handling is different
                // buttons without value
                if (el.value === '') {
                    const tooltip = this.getTitleTranslation(tool);
                    if (tooltip != null) {
                        el.setAttribute('title', tooltip);
                    }
                } else if (typeof el.value !== 'undefined') {
                    // buttons with value
                    const tooltip = this.getTitleTranslation(tool + '.' + el.value);
                    if (tooltip != null) {
                        el.setAttribute('title', tooltip);
                    }
                } else {
                    // default
                    const tooltip = this.getTitleTranslation(tool);
                    el.setAttribute('title', tooltip);
                }
            }
        };

        const quillEditorComponent = this.quillEditorDomElement.nativeElement;

        if (quillEditorComponent != null) {
            const toolbarElement = quillEditorComponent.querySelector('.ql-toolbar');
            if (toolbarElement != null) {
                const matchesButtons = toolbarElement.querySelectorAll('button');
                matchesButtons.forEach(el => {
                    showTooltip('button', el);
                });

                // for submenus inside
                const matchesSpans = toolbarElement.querySelectorAll('.ql-toolbar > span > span');
                matchesSpans.forEach(el => {
                    showTooltip('span', el);
                });
            }
        }
    }

    private uploadBase64ImageToBlob(base64: string): void {
        // Initialize upload dialog
        const modalRef = this.modalService.open(FileUploadDialogComponent, {
            windowClass: 'invisible',
        });

        const fileUploadDialog = <FileUploadDialogComponent>modalRef.componentInstance;
        fileUploadDialog.maxInputSizeInBytes = this.maxInputSizeForRichText;
        fileUploadDialog.base64StringToUpload = base64;
        fileUploadDialog.blobType = BlobType.EmbeddedImages;
        fileUploadDialog.pathToAppend = 'embedded-image-' + new Date().getTime();

        modalRef.result
            .then((fileUrl: string) => {
                this.embedImageIntoEditor(fileUrl);
            })
            .catch(() => {
                // do nothing
            });
    }

    private customImageUpload(): void {
        const modalRef = this.modalService.open(FileUploadDialogComponent);
        const fileUploadDialog = <FileUploadDialogComponent>modalRef.componentInstance;

        fileUploadDialog.maxInputSizeInBytes = this.maxInputSizeForRichText;
        fileUploadDialog.blobType = BlobType.EmbeddedImages;
        fileUploadDialog.pathToAppend = 'embedded-image-' + new Date().getTime();

        modalRef.result
            .then((fileUrl: string) => {
                this.embedImageIntoEditor(fileUrl);
            })
            .catch(() => {
                // do nothing
            });
    }

    private embedImageIntoEditor(fileUrl: string): void {
        const index = (this.quillEditorRef.getSelection() || {}).index || this.quillEditorRef.getLength();
        if (index != null) {
            this.quillEditorRef.insertEmbed(index, 'image', fileUrl, 'user');
        }
    }

    /**
     * Translates the button tooltip.
     *
     * @param elementTitle - The class of the buttons is translated in the language file.
     */
    private getTitleTranslation(elementTitle?: string): string | undefined {
        if (elementTitle != null) {
            elementTitle = elementTitle.toUpperCase();
            return this.translateService.instant('RICH_TEXT.BUTTONS.' + elementTitle);
        }
    }
}
